<template>
  <div class="standardpage">
    <b-card>
      <b-card-header>
        <b-row>
          <b-col cols="12" sm="6" class="text-right text-sm-left pt-1">
            <i :class="refundreport.icon" class="mr-1"></i>
            <span>{{ refundreport.title }}</span></b-col>
        </b-row>
      </b-card-header>

      <b-alert :show="!!refundreport.error" variant="danger" class="mx-3 mb-2">
        <strong>The report could not be loaded.</strong>
        {{ refundreport.error }}
        <div class="small mt-1">
          Nothing is shown below, because showing the previous filter's rows here would let them
          be exported as though they matched the dates on screen.
        </div>
      </b-alert>

      <div class="availtable datatable">
        <b-card v-if="!showfilter" class="w-100pct mb-2">
          <b-card-body class="py-0">
            <b-row>
              <b-col cols="12" sm="6" md="4" xl="3">
                <b-form-group label-cols="12" label="Refund Date" label-size="sm">
                  <b-input-group>
                    <daterangepicker size="sm" ref="refund_at" v-model="refund_at" :opens="'right'"
                      :locale-data="locale_data" :ranges="locale_ranges" :showDropdowns="true" :linkedCalendars="true"
                      :single-date-picker="'range'">
                    </daterangepicker>
                    <b-input-group-append>
                      <b-button size="sm" variant="outline-secondary" @click="
                        refund_at = {
                          startDate: null,
                          endDate: null,
                        }
                        ">
                        <i class="fa fa-remove"></i>
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-col>

              <b-col cols="12" sm="6" md="4" xl="3">
                <b-form-group label-cols="12" label="PNR / Locator" label-size="sm">
                  <b-input-group>
                    <b-form-input v-model="locator" :debounce="refundreport.debounce" placeholder="PNR / Locator"
                      trim></b-form-input>
                    <b-input-group-append>
                      <b-button size="sm" variant="outline-secondary" :disabled="!locator" @click="locator = ''">
                        <i class="fa fa-remove"></i>
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-col>

              <b-col cols="12" sm="6" md="4" xl="3">
                <b-form-group label-cols="12" label="Ticket No" label-size="sm">
                  <b-input-group>
                    <b-form-input v-model="ticketno" :debounce="refundreport.debounce" placeholder="Ticket No"
                      trim></b-form-input>
                    <b-input-group-append>
                      <b-button size="sm" variant="outline-secondary" :disabled="!ticketno" @click="ticketno = ''">
                        <i class="fa fa-remove"></i>
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>

        <div class="px-3">
          <d-table classdivtable="availtable datatable" refid="refundreport" :icon="refundreport.icon"
            :toolbarlibrary="refundreport.toolbarlibrary" :tools="refundreport.tools" :badges="refundreport.badges"
            :fields="refundreport.fields" :items="refundreport.items" :busy="refundreport.busy"
            :offset="refundreport.offset" :limit="refundreport.limit" :page="refundreport.page" :showpages="true"
            @load="refundreport_load" :downloaded="refundreport.downloaded" @click="refundreport_click"
            @select="refundreport_select">
          </d-table>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import gql from "graphql-tag";

export default {
  name: "RefundReport",
  watch: {
    async refund_at() {
      await this.refundreport_load(0);
    },
    async locator() {
      await this.refundreport_load(0);
    },
    async ticketno() {
      await this.refundreport_load(0);
    },
  },
  data() {
    return {
      showfilter: false,
      refund_at: {
        startDate: this.$moment().startOf("month").format("YYYY-MM-DD"),
        endDate: this.$moment().format("YYYY-MM-DD"),
      },
      locator: null,
      ticketno: null,
      refundreport: {
        title: "Refund Report",
        icon: "fa fa-money text-primary",
        debounce: 500,
        busy: false,
        page: 1,
        offset: 0,
        // refundreport has no server-side offset; the full date-bounded set is loaded and
        // shown in one grid. A large limit ("All" = 5000) keeps it to a single page. This
        // is the fixed display limit and refundreport_load normalises any injected paging
        // limit back to it. See callouts / refundreport_load below.
        limit: 5000,
        selectall: false,
        toolbarlibrary: [],
        // download -> d-table's built-in export modal (xlsx / tab-txt / csv);
        // clipboard -> copy grid; filter -> toggle the filter card.
        tools: "download,clipboard,filter",
        fields: [
          {
            key: "pnr",
            label: "PNR",
            sortable: true,
          },
          {
            key: "refund_date",
            label: "Refund Date",
            sortable: true,
            class: "text-center",
            formatter: (value, key, item) => {
              if (!value) return "";
              let m = this.$moment(value, [
                "YYYY-MM-DD",
                "DD/MM/YYYY",
                this.$moment.ISO_8601,
              ]);
              return m.isValid() ? m.format("DD/MM/YYYY") : value;
            },
          },
          {
            key: "refund_ticketno",
            label: "Refund Ticket No",
            sortable: true,
          },
          {
            key: "bsp_link",
            label: "BSP Link",
            sortable: true,
          },
          {
            key: "passenger",
            label: "Passenger",
            sortable: true,
          },
          {
            key: "base_fare",
            label: "Base Fare",
            sortable: true,
            class: "text-right",
            formatter: (value, key, item) => this.money(value),
          },
          {
            key: "tax",
            label: "Tax",
            sortable: true,
            class: "text-right",
            formatter: (value, key, item) => this.money(value),
          },
          {
            key: "slac",
            label: "Client SL A/C",
            sortable: true,
          },
          {
            key: "accountcode",
            label: "Account Code",
            sortable: true,
          },
          {
            key: "eventcode",
            label: "Event Code",
            sortable: true,
          },
          {
            key: "refund_platform",
            label: "Refund Platform",
            sortable: true,
          },
          {
            key: "is_etr",
            label: "Is ETR",
            sortable: true,
            class: "text-center",
          },
          {
            key: "etr_refund_to_client",
            label: "ETR Refund To Client",
            sortable: true,
            class: "text-right",
          },
          {
            key: "refund_reason",
            label: "Refund Reason",
            sortable: true,
          },
          {
            key: "client_grand_total_refund",
            label: "Client Grand Total Refund",
            sortable: true,
            class: "text-right",
            formatter: (value, key, item) => this.money(value),
          },
        ],
        badges: [],
        items: [],
        selected: [],
        denied: [],
        downloaded: [],
        error: null,
      },
    };
  },
  async mounted() {
    if (!this.permitted()) return;
    this.accessdenied();
  },
  async activated() {
    if (!this.permitted()) return;
    await this.refundreport_load();
  },
  methods: {
    // money-format a decimal string (backend returns pounds.pence as varchar; may be
    // negative for refunds). Non-numeric / null -> blank.
    money(value) {
      if (value === null || value === undefined || value === "") return "";
      let n = Number(value);
      if (isNaN(n)) return value;
      return n.toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    // The route is reachable by typing the URL even though the sidebar entry is hidden
    // (TheSidebar.vue gates it on navigation.accounts), and the server is NOT a backstop:
    // userpermission() only gates by endpoint for api-role accounts, and even that check is
    // dead code (bop-documentation#141). So without this, any signed-in user could open the
    // report and export every client's refunds. Gated on the same permission as the sidebar.
    permitted() {
      if (this.permissionvalid("navigation.accounts")) return true;
      this.$router.replace("/dashboard");
      return false;
    },
    accessdenied() {
      this.refundreport.denied = [];
    },
    async refundreport_load(offset, limit, page) {
      let state = this.refundreport;
      state.error = null;

      // This is a single, non-paginated report: the full date-bounded set is fetched
      // at once and shown in one grid (limit "All" = 5000). refundreport has no
      // server-side offset and returns no total-rows field, so paging cannot work.
      //
      // d-table drives the shared toolbar and injects finite paging limits that would
      // otherwise pin the server fetch and silently hide rows:
      //   - its export dialog's Back/Cancel emits load(0, 15, 1);
      //   - its page-size selector emits load(0, <size>, 1).
      // We therefore deliberately ignore the injected offset/page and normalise any
      // non-zero limit back to the full display cap, so opening/closing the export
      // dialog (or changing page size) can never truncate the grid.
      //
      // The one preserved value is limit === 0 — our own download-all sentinel, which
      // sends an uncapped query (limit: null) and populates state.downloaded.
      state.offset = 0;
      state.page = 1;
      state.limit = limit === 0 ? 0 : 5000;

      state.busy = true;

      let hasrange = !!(this.refund_at.startDate || this.refund_at.endDate);
      let variables = {
        // refundreport takes no offset; when limit==0 (download-all) send null = uncapped.
        limit: state.limit == 0 ? null : state.limit,
        locator: this.locator ? this.locator : null,
        ticketno: this.ticketno ? this.ticketno : null,
        // Send dateissued ONLY when a range is actually set. Sending [null, null] looks
        // date-bounded to the server (a truthy array), which skips the 2000-row cap in the
        // `else if (!args.dateissued)` branch and lets Download All shred the whole PNRs
        // table uncapped. Omitting it lets that guard do its job.
        dateissued: hasrange
          ? [
              this.refund_at.startDate
                ? this.$moment(this.refund_at.startDate).format("YYYY-MM-DD")
                : null,
              this.refund_at.endDate
                ? this.$moment(this.refund_at.endDate).format("YYYY-MM-DD")
                : null,
            ]
          : null,
      };
      console.log("variables", variables);
      try {
        const response = await this.$apollo.query({
          query: gql`
            query refundreport(
              $dateissued: [String]
              $locator: String
              $ticketno: String
              $limit: Int
            ) {
              refundreport(
                dateissued: $dateissued
                locator: $locator
                ticketno: $ticketno
                limit: $limit
              ) {
                id
                pnr
                refund_date
                refund_ticketno
                bsp_link
                passenger
                base_fare
                tax
                slac
                accountcode
                eventcode
                refund_platform
                is_etr
                etr_refund_to_client
                refund_reason
                client_grand_total_refund
              }
            }
          `,
          variables,
        });

        state.downloaded = [];
        if (limit === 0) {
          state.downloaded = response.data.refundreport;
        } else {
          state.items = response.data.refundreport;
        }
      } catch (error) {
        console.log(error);
        // Clear rather than leave the previous filter's rows on screen. They would still be
        // exportable, and the footer count and date box would both claim they matched the
        // current filter.
        state.error =
          (error && error.message) || "The request did not complete.";
        state.items = [];
        state.downloaded = [];
      }
      state.busy = false;
    },
    async refundreport_click(a, ss) {
      console.log("refundreport_click", a, ss);

      let state = this.refundreport;

      switch (a) {
        case "download_view":
          state.downloaded = [];
          state.downloaded = state.items;
          break;

        case "download_all":
          state.downloaded = [];
          await this.refundreport_load(0, 0);
          break;

        case "filter":
          this.showfilter = !this.showfilter;
          break;
      }
    },
    async refundreport_select(ss, tools) {
      console.log("refundreport_select", ss);

      let state = this.refundreport;
      state.selected = ss;
      let tool = null;

      if (tools) {
        let enabled = ["download", "filter", "clipboard"];
        enabled.forEach(function (action) {
          tool = tools.find((o) => o.action == action);
          if (tool) {
            tool.disabled = false;
          }
        });
        if (!state.denied) {
          state.denied = [];
        }
        state.denied.forEach(function (action) {
          tool = tools.find((o) => o.action == action);
          if (tool) {
            tool.visible = false;
          }
        });
      }
    },
  },
};
</script>

<style></style>
